import React, { useState } from 'react'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { classNames } from '../../utils/classNames'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
  variant?: 'default' | 'error' | 'success'
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className = '',
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = true,
      variant = 'default',
      id,
      type = 'text',
      'aria-describedby': ariaDescribedby,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false)
    const isPassword = type === 'password'
    const inputType = isPassword && showPassword ? 'text' : type
    const hasRightContent = rightIcon || isPassword
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`
    const errorId = error ? `${inputId}-error` : undefined
    const helperId = helperText && !error ? `${inputId}-helper` : undefined
    
    // Combine aria-describedby with error and helper text IDs
    const describedBy = [
      ariaDescribedby,
      errorId,
      helperId
    ].filter(Boolean).join(' ')
    
    const inputClasses = classNames(
      'input',
      {
        'input-error': variant === 'error' || error,
        'border-success-500 focus:ring-success-500 focus:border-success-500': variant === 'success',
        'pl-10': leftIcon,
        'pr-10': hasRightContent,
      },
      className
    )

    const containerClasses = classNames(
      'relative',
      fullWidth && 'w-full'
    )

    return (
      <div className={containerClasses}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
          >
            {label}
            {props.required && (
              <span className="text-danger-500 ml-1" aria-label="required">
                *
              </span>
            )}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div 
              className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              aria-hidden="true"
            >
              <div className="h-5 w-5 text-neutral-400 dark:text-neutral-500">
                {leftIcon}
              </div>
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            className={inputClasses}
            aria-invalid={ariaInvalid !== undefined ? ariaInvalid : !!error}
            aria-describedby={describedBy || undefined}
            aria-errormessage={errorId}
            {...props}
          />
          
          {isPassword ? (
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors" />
              ) : (
                <EyeIcon className="h-5 w-5 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors" />
              )}
            </button>
          ) : rightIcon ? (
            <div 
              className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              aria-hidden="true"
            >
              <div className="h-5 w-5 text-neutral-400 dark:text-neutral-500">
                {rightIcon}
              </div>
            </div>
          ) : null}
        </div>
        
        {(error || helperText) && (
          <div className="mt-1">
            {error && (
              <p 
                id={errorId}
                className="text-sm text-danger-600 dark:text-danger-400"
                role="alert"
                aria-live="polite"
              >
                {error}
              </p>
            )}
            {helperText && !error && (
              <p 
                id={helperId}
                className="text-sm text-neutral-500 dark:text-neutral-400"
              >
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
